import { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { categories } from '@/mocks/categories';

export default function Navbar() {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleShoppingGuide = () => {
    setMobileMenuOpen(false);
    const el = document.getElementById('categories');
    if (el) {
      el.scrollIntoView({ behavior: 'smooth' });
    } else {
      navigate('/');
    }
  };

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? 'bg-background-50/95 backdrop-blur-md border-b border-background-200/70 shadow-sm'
          : 'bg-background-50/85 backdrop-blur-sm border-b border-background-200/40'
      }`}
    >
      <div className="w-full px-4 md:px-6 lg:px-10">
        <div className="flex items-center justify-between h-16">
          <div className="flex items-center gap-6">
            <Link
              to="/"
              className="font-heading text-xl font-extrabold text-foreground-950 whitespace-nowrap"
            >
              LitBuy Spreadsheets
            </Link>

            <div className="hidden md:flex items-center gap-1">
              <Link
                to="/"
                className="text-sm font-semibold text-foreground-800 hover:text-primary-600 transition-colors cursor-pointer whitespace-nowrap px-3 py-2 rounded-lg hover:bg-primary-50"
              >
                Home
              </Link>
              <button
                onClick={handleShoppingGuide}
                className="text-sm font-semibold text-foreground-800 hover:text-accent-600 transition-colors cursor-pointer whitespace-nowrap px-3 py-2 rounded-lg hover:bg-accent-50"
              >
                Shopping Guide
              </button>
            </div>
          </div>

          <div className="hidden md:flex items-center gap-3" ref={dropdownRef}>
            <div className="relative">
              <button
                onClick={() => setDropdownOpen(!dropdownOpen)}
                className="flex items-center gap-1.5 text-sm font-semibold text-foreground-800 hover:text-primary-600 transition-colors cursor-pointer whitespace-nowrap py-2 px-3 rounded-lg hover:bg-primary-50"
              >
                <span>Browse Categories</span>
                <span className={`w-4 h-4 flex items-center justify-center transition-transform duration-200 ${dropdownOpen ? 'rotate-180' : ''}`}>
                  <i className="ri-arrow-down-s-line text-sm"></i>
                </span>
              </button>

              {dropdownOpen && (
                <div className="absolute top-full left-0 mt-2 w-72 bg-background-50 rounded-xl border border-background-200/70 shadow-lg py-3 max-h-96 overflow-y-auto">
                  {categories.map((cat) => (
                    <a
                      key={cat.name}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      className="flex items-center gap-3 px-4 py-2.5 text-sm text-foreground-700 hover:bg-primary-50 hover:text-primary-700 transition-colors cursor-pointer"
                      onClick={() => setDropdownOpen(false)}
                    >
                      <span className="w-8 h-8 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600 flex-shrink-0">
                        <i className={`${cat.icon} text-sm`}></i>
                      </span>
                      <span>{cat.name}</span>
                    </a>
                  ))}
                </div>
              )}
            </div>

            <a
              href="https://kaspreadsheet.com/"
              target="_blank"
              rel="noopener nofollow"
              className="text-sm font-semibold text-foreground-800 hover:text-primary-600 transition-colors cursor-pointer whitespace-nowrap px-3 py-2"
            >
              LitBuy Spreadsheet
            </a>

            <a
              href="https://kaspreadsheet.com/"
              target="_blank"
              rel="noopener nofollow"
              className="inline-flex items-center px-5 py-2.5 text-sm font-bold bg-primary-500 text-background-50 rounded-full hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap shadow-sm"
            >
              Buy It Now
            </a>
          </div>

          <button
            className="md:hidden w-10 h-10 flex items-center justify-center cursor-pointer text-foreground-800 rounded-lg hover:bg-background-100 transition-colors"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            <i className={`${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-xl`}></i>
          </button>
        </div>
      </div>

      {mobileMenuOpen && (
        <div className="md:hidden bg-background-50 border-t border-background-200/70">
          <div className="px-4 py-4 space-y-4">
            <div className="flex items-center gap-3 pb-3 border-b border-background-200/70">
              <Link
                to="/"
                onClick={() => setMobileMenuOpen(false)}
                className="flex-1 text-center py-2.5 text-sm font-semibold text-foreground-800 bg-background-100 rounded-lg hover:bg-primary-50 hover:text-primary-700 transition-colors cursor-pointer"
              >
                <span className="w-4 h-4 inline-flex items-center justify-center mr-1.5"><i className="ri-home-4-line text-sm"></i></span>
                Home
              </Link>
              <button
                onClick={handleShoppingGuide}
                className="flex-1 text-center py-2.5 text-sm font-semibold text-foreground-800 bg-background-100 rounded-lg hover:bg-accent-50 hover:text-accent-700 transition-colors cursor-pointer"
              >
                <span className="w-4 h-4 inline-flex items-center justify-center mr-1.5"><i className="ri-guide-line text-sm"></i></span>
                Shopping Guide
              </button>
            </div>

            <div>
              <p className="text-xs font-semibold text-foreground-500 uppercase tracking-wider mb-3">Product Categories</p>
              <div className="grid grid-cols-2 gap-1.5">
                {categories.map((cat) => (
                  <a
                    key={cat.name}
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    className="flex items-center gap-2 px-3 py-2.5 text-sm text-foreground-700 hover:bg-primary-50 hover:text-primary-700 rounded-lg transition-colors cursor-pointer"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    <span className="w-6 h-6 flex items-center justify-center rounded-md bg-primary-100 text-primary-600 flex-shrink-0">
                      <i className={`${cat.icon} text-xs`}></i>
                    </span>
                    <span className="text-xs font-medium truncate">{cat.name}</span>
                  </a>
                ))}
              </div>
            </div>

            <div className="flex flex-col gap-2 pt-3 border-t border-background-200/70">
              <a
                href="https://kaspreadsheet.com/"
                target="_blank"
                rel="noopener nofollow"
                className="text-center py-2.5 text-sm font-semibold text-foreground-800 hover:text-primary-600 transition-colors cursor-pointer"
              >
                LitBuy Spreadsheet
              </a>
              <a
                href="https://kaspreadsheet.com/"
                target="_blank"
                rel="noopener nofollow"
                className="text-center py-3 text-sm font-bold bg-primary-500 text-background-50 rounded-full hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                Buy It Now
              </a>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}